import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import Preloader from '../../components/Preloader';
import api from '../../services/api';
import { Link } from 'react-router-dom';

const DoctorCases = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      console.log('[DoctorCases] User logged in:', user.name, 'ID:', user.id);
      setLoading(true);
      Promise.all([loadAssignments(), loadProtocols()])
        .then(() => {
          console.log('[DoctorCases] Initial data loading complete');
          setLoading(false);
        })
        .catch((err) => {
          console.error('[DoctorCases] Error loading initial data:', err);
          setLoading(false);
        });
    }
  }, [user]);

  const loadAssignments = async () => {
    try {
      console.log('[DoctorCases] Fetching assignments for doctor ID:', user.id);
      const response = await api.get(`/assignments?doctor_id=${user.id}`);
      console.log('[DoctorCases] Assignments response:', response.data);
      setAssignments(response.data.assignments || []);
    } catch (error) {
      console.error('[DoctorCases] Error loading assignments:', error);
      setAssignments([]);
    }
  };

  const loadProtocols = async () => {
    try {
      console.log('[DoctorCases] Fetching protocols');
      const response = await api.get('/protocols');
      console.log('[DoctorCases] Protocols response:', response.data);
      setProtocols(response.data.protocols || []);
    } catch (error) {
      console.error('[DoctorCases] Error loading protocols:', error);
      setProtocols([]);
    }
  };

  // Simplified loadCases function - just show basic case assignments
  const loadCases = async () => {
    try {
      // Get doctor's cases
      console.log('[DoctorCases] Fetching cases from /cases/view');
      const response = await api.get('/cases/view');
      console.log('[DoctorCases] Cases response:', response.data);
      
      const casesData = response.data.cases || [];
      console.log('[DoctorCases] casesData length:', casesData.length);
      console.log('[DoctorCases] assignments length:', assignments.length);

      // Map assignment IDs to the cases returned from /cases/view
      const enhancedCases = casesData.map((caseItem) => {
        // Find matching assignment for this case
        const assignment = assignments.find(a => a.case_id === caseItem.id);
        
        if (!assignment) return null;
        
        return {
          id: caseItem.id,
          assignedAt: assignment?.assigned_at || null,
          assignmentId: assignment?.id,
          customerID: caseItem.customer_id
        };
      }).filter(Boolean);

      console.log('[DoctorCases] enhancedCases length:', enhancedCases.length);
      setCases(enhancedCases);
    } catch (error) {
      console.error('[DoctorCases] Error loading cases:', error);
      console.error('[DoctorCases] Error details:', error.response?.data || error.message);
      setCases([]);
    }
  };

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    
    try {
      const date = new Date(dateString);
      // Check if the date is valid
      return date instanceof Date && !isNaN(date) ?
        date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }) :
        'Invalid date format';
    } catch (error) {
      return 'Date error';
    }
  };

  // Add this effect to reload cases when assignments or protocols change
  useEffect(() => {
    console.log('[DoctorCases] Dependencies changed - assignments:', assignments.length, 'protocols:', protocols.length);
    if (assignments.length > 0 && protocols.length > 0) {
      console.log('[DoctorCases] Loading cases...');
      setLoading(true);
      loadCases().then(() => {
        console.log('[DoctorCases] Cases loaded, setting loading to false');
        setLoading(false);
      }).catch((err) => {
        console.error('[DoctorCases] Error in loadCases promise:', err);
        setLoading(false);
      });
    }
  }, [assignments, protocols]);

  if (loading) {
    return (
      <ResearchLayout pageTitle="My Assigned Cases">
        <Preloader message="Loading your assigned cases..." />
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="My Assigned Cases">
      <div className="container-fluid">

        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-users me-2"></i>
                  My Cases ({cases.length})
                </h5>
              </div>
              <div className="card-body">
                {cases.length > 0 ? (
                  <div className="row">
                    {cases.map((caseData) => (
                      <div key={`${caseData.id}-${caseData.assignmentId}`} className="col-lg-4 col-md-6 mb-4">
                        <div className="card border border-secondary h-100">
                          <div className="card-header bg-light text-center">
                            <h6 className="mb-0 text-secondary fw-bold">
                              <i className="fas fa-user me-2"></i>
                              Case ID: {caseData.id}
                            </h6>
                          </div>
                          <div className="card-body text-center">
                            {/* Assigned Date */}
                            <div className="mb-4">
                              <small className="text-muted">
                                <i className="fas fa-calendar me-1"></i>
                                Assigned: {formatDate(caseData.assignedAt)}
                              </small>
                            </div>
                            
                            {/* Start Questions Button */}
                            <div className="mt-auto">
                              <Link
                                to={`/doctor/answer-questions/start?case=${caseData.id}`}
                                className="btn btn-success btn-sm w-100"
                              >
                                <i className="fas fa-play me-1"></i>
                                Start Questions
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-users fa-4x text-muted mb-4"></i>
                    <h4 className="text-muted">No Assigned Cases</h4>
                    <p className="text-muted">
                      You haven't been assigned any cases yet. Check back later or contact your Medical Admin.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default DoctorCases;